import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  deleteCategory,
  addCategory,
  updateCategory,
} from "../../features/categories/categoriesSlice";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { FiPlus } from "react-icons/fi";

const CategoryManager = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { items, loading } = useSelector((state) => state.categories);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({ name_ar: "", name_en: "", image: null });

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleDelete = (id) => {
    Swal.fire({
      title: t("CategoryManager.confirmDelete"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("CategoryManager.yes"),
      cancelButtonText: t("CategoryManager.cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCategory(id));
      }
    });
  };

  const handleEditClick = (category) => {
    setEditingCategory(category);
    setForm({
      name_ar: category.name_ar || "",
      name_en: category.name_en || "",
      image: category.image || null,
    });
    setShowModal(true);
  };

  const handleAddClick = () => {
    setEditingCategory(null);
    setForm({ name_ar: "", name_en: "", image: null });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("name_ar", form.name_ar);
    formData.append("name_en", form.name_en);
    if (form.image instanceof File) {
      formData.append("image", form.image);
    }

    if (editingCategory) {
      formData.append("_method", "put");
      dispatch(
        updateCategory({ id: editingCategory.id, updatedData: formData })
      ).then(() => {
        dispatch(fetchCategories());
        setShowModal(false);
      });
    } else {
      dispatch(addCategory(formData)).then(() => {
        dispatch(fetchCategories());
        setShowModal(false);
      });
    }
  };

  return (
    <div className="container my-2 mt-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="">
          {t("CategoryManager.dashboardCategories")}
          <span className="mx-2">({items.length})</span>
        </h2>

        <button className="btn btn-info " onClick={handleAddClick}>
          <FiPlus className="me-2" />
          {t("CategoryManager.add")}
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show fade d-block" tabIndex="-1">
          <div className="modal-dialog">
            <form onSubmit={handleSubmit} className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCategory
                    ? t("CategoryManager.update")
                    : t("CategoryManager.add")}
                </h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    {t("CategoryManager.form.name_ar")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name_ar"
                    value={form.name_ar}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {t("CategoryManager.form.name_en")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name_en"
                    value={form.name_en}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {t("CategoryManager.form.image")}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    name="image"
                    onChange={handleChange}
                  />
                  {editingCategory &&
                    form.image &&
                    typeof form.image === "string" && (
                      <img
                        src={form.image}
                        alt="Preview"
                        className="mt-2"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-info">
                  {editingCategory
                    ? t("CategoryManager.saveChanges")
                    : t("CategoryManager.create")}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {t("CategoryManager.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p className="fs-5 mt-4">{t("CategoryManager.loading")}</p>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <table className="table table-striped align-middle text-center mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>{t("CategoryManager.form.name_ar")}</th>
                <th>{t("CategoryManager.form.name_en")}</th>
                <th>{t("CategoryManager.form.image")}</th>
                <th>{t("CategoryManager.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-muted py-4">
                    {t("CategoryManager.noCategories")}
                  </td>
                </tr>
              ) : (
                items.map((cat, index) => (
                  <tr key={cat.id}>
                    <td>{index + 1}</td>
                    <td>{cat.name_ar}</td>
                    <td>{cat.name_en}</td>
                    <td>
                      <img
                        src={cat.image}
                        alt="category"
                        style={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-light mx-2"
                        onClick={() => handleEditClick(cat)}
                        title={t("CategoryManager.edit")}
                      >
                        <i className="fa-solid fa-pen-to-square text-primary"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-light mx-2"
                        onClick={() => handleDelete(cat.id)}
                        title={t("CategoryManager.delete")}
                      >
                        <i className="fa-solid fa-trash-can text-danger"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
